/**
 * P12B: Global Holographic Market Map.
 *
 * Interactive 3D globe rendered by react-globe.gl (Three.js under the hood).
 * Each region is a glowing ring whose color = sentiment (bull/bear/neutral)
 * and height = volatility. Click a region → side panel with top companies
 * + macro drivers. Auto-rotate on when operator has no interaction for 5s.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Globe from 'react-globe.gl';
import api from '../lib/api';
import CornerPanel from '../components/CornerPanel';
import { Earth, RotateCw, Pause } from 'lucide-react';
import { cstDateTime } from '../lib/time';

const SENTIMENT_COLOR = {
  bullish: '#4ade80',   // green
  bearish: '#f87171',   // red
  neutral: '#fde68a',   // yellow
};

export default function GlobePage() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [autoRotate, setAutoRotate] = useState(true);
  const globeRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/globe/regions?_t=${Date.now()}`);
      setData(data);
    } catch {}
  };

  useEffect(() => { load(); const iv = setInterval(load, 45000); return () => clearInterval(iv); }, []);

  // Enable / disable auto-rotate on the underlying Three.js OrbitControls.
  useEffect(() => {
    if (!globeRef.current) return;
    const controls = globeRef.current.controls?.();
    if (controls) {
      controls.autoRotate = autoRotate;
      controls.autoRotateSpeed = 0.6;
    }
  }, [autoRotate, data]);

  // Set a dark atmosphere once the globe is ready.
  useEffect(() => {
    const g = globeRef.current;
    if (!g) return;
    const controls = g.controls?.();
    if (controls) {
      controls.enableDamping = true;
      controls.dampingFactor = 0.1;
    }
  }, [data]);

  const points = useMemo(() => {
    if (!data) return [];
    return data.regions.map((r) => ({
      ...r,
      size: 0.6 + (r.volatility_index || 0) / 50,
      color: SENTIMENT_COLOR[r.sentiment] || SENTIMENT_COLOR.neutral,
    }));
  }, [data]);

  const openRegion = async (region) => {
    setSelected(region);
    setAutoRotate(false);
    try {
      const { data } = await api.get(`/globe/regions/${region.id}?_t=${Date.now()}`);
      setDetail(data);
    } catch { setDetail(null); }
    // Smoothly fly to region
    if (globeRef.current) {
      globeRef.current.pointOfView({ lat: region.lat, lng: region.lng, altitude: 1.8 }, 1200);
    }
  };

  return (
    <div className="p-4 space-y-3">
      <CornerPanel
        title="Global Market Map // holographic intelligence layer"
        right={
          <div className="flex items-center gap-2">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--txt-secondary)]">
              {data ? `${data.regions.length} regions` : 'loading...'}
            </span>
            <button
              data-testid="globe-autorotate-toggle"
              onClick={() => setAutoRotate((v) => !v)}
              className={`flex items-center gap-1 text-[11px] tracking-[0.2em] uppercase border px-2 py-1 ${
                autoRotate ? 'text-buy border-buy' : 'text-[var(--txt-secondary)] border-[var(--border-muted)]'
              }`}
            >
              {autoRotate ? <><RotateCw size={11} /> rotating</> : <><Pause size={11} /> paused</>}
            </button>
          </div>
        }
        testId="globe-panel"
      >
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-3">
          {/* GLOBE */}
          <div
            data-testid="globe-canvas"
            className="relative bg-black border border-[var(--border-default)] overflow-hidden"
            style={{ height: 560 }}
          >
            <Globe
              ref={globeRef}
              width={undefined}
              height={558}
              backgroundColor="rgba(0,0,0,0)"
              globeImageUrl="//unpkg.com/three-globe/example/img/earth-night.jpg"
              bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
              atmosphereColor="#08f7fe"
              atmosphereAltitude={0.25}
              pointsData={points}
              pointLat="lat"
              pointLng="lng"
              pointColor="color"
              pointAltitude={(d) => (d.volatility_index || 20) / 100}
              pointRadius="size"
              pointLabel={(d) => `
                <div style="background:rgba(0,0,0,.85);border:1px solid ${d.color};padding:6px 8px;font-family:monospace;font-size:11px;color:#fff;">
                  <div style="color:${d.color};font-weight:bold">${d.name}</div>
                  <div>sentiment: ${d.sentiment}</div>
                  <div>vol idx:   ${d.volatility_index}</div>
                  <div>bulls:     ${d.bull_count}</div>
                  <div>bears:     ${d.bear_count}</div>
                </div>`}
              onPointClick={openRegion}
              ringsData={points}
              ringLat="lat"
              ringLng="lng"
              ringColor={(d) => () => d.color}
              ringMaxRadius={2 + (Math.random() * 0.5)}
              ringPropagationSpeed={2}
              ringRepeatPeriod={1800}
            />
            {/* Legend */}
            <div className="absolute bottom-2 left-2 bg-black/70 border border-[var(--border-default)] px-2 py-1 font-mono text-[10px] tracking-wider flex items-center gap-3">
              <span><span className="inline-block w-2 h-2 rounded-full bg-[#4ade80] mr-1" />BULLISH</span>
              <span><span className="inline-block w-2 h-2 rounded-full bg-[#f87171] mr-1" />BEARISH</span>
              <span><span className="inline-block w-2 h-2 rounded-full bg-[#fde68a] mr-1" />NEUTRAL</span>
            </div>
          </div>

          {/* DETAIL PANEL */}
          <div className="border border-[var(--border-default)] bg-black p-3 min-h-[560px]">
            {!selected ? (
              <div className="h-full flex flex-col items-center justify-center text-[var(--txt-muted)] text-[12px] font-mono gap-2">
                <Earth size={40} className="text-cyan-sys" />
                <span>// click a region to inspect</span>
              </div>
            ) : (
              <div className="space-y-3 font-mono">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-sys">region</div>
                  <div className="text-lg">{selected.name}</div>
                  <div className="text-[11px] text-[var(--txt-secondary)]">{selected.city}</div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[12px]">
                  <Stat label="sentiment"  value={selected.sentiment?.toUpperCase()} color={SENTIMENT_COLOR[selected.sentiment]} />
                  <Stat label="vol index"  value={selected.volatility_index ?? '—'} />
                  <Stat label="avg confl"  value={selected.avg_confluence ?? '—'} />
                  <Stat label="avg ATR%"   value={selected.avg_atr_pct ?? '—'} />
                  <Stat label="bulls"      value={selected.bull_count ?? 0} color="#4ade80" />
                  <Stat label="bears"      value={selected.bear_count ?? 0} color="#f87171" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-sys mb-1">macro drivers</div>
                  {(detail?.macro_drivers || []).length ? (
                    <ul className="text-[11px] space-y-1 text-[var(--txt-primary)]">
                      {(detail.macro_drivers || []).map((d, i) => <li key={i}>▸ {d}</li>)}
                    </ul>
                  ) : <div className="text-[var(--txt-muted)] text-[11px]">// insight computing...</div>}
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-cyan-sys mb-1">top companies</div>
                  <div className="space-y-1">
                    {(selected.companies || []).slice(0, 10).map((c) => (
                      <div key={c.symbol} className="flex items-center justify-between text-[11px] border-b border-[var(--border-default)] py-1">
                        <span>{c.symbol}</span>
                        <span className={
                          c.signal === 'BUY' ? 'text-buy' :
                          c.signal === 'SELL' ? 'text-sell' :
                          c.signal === 'HOLD' ? 'text-hold' : 'text-[var(--txt-muted)]'
                        }>
                          {c.signal || '—'}
                        </span>
                        <span className="text-[var(--txt-muted)]">{c.confluence ?? '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CornerPanel>
      {data?.timestamp && (
        <div className="text-right text-[10px] text-[var(--txt-muted)] tracking-wider font-mono">
          last updated {cstDateTime(data.timestamp)}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="border border-[var(--border-default)] p-2">
      <div className="text-[9px] tracking-[0.2em] uppercase text-[var(--txt-secondary)]">{label}</div>
      <div className="text-sm" style={color ? { color } : undefined}>{value}</div>
    </div>
  );
}
